import { ALERT_ERROR, FULFILLED, REJECTED } from 'actionTypes';
import history from 'appHistory';
import { delay, put, select } from 'redux-saga/effects';
import { apiRequest, apiRequestDirectURL } from 'utils/request';

const SEARCH_INPUT_DELAY = 300;

export const handlerError = function*(TYPE, error) {
  if (error.response) {
    if (error.response.status === 401) {
      yield put({ type: ALERT_ERROR, payload: error.response.data.detail });
      yield put({ type: 'LOGOUT' });
    } else {
      yield put({ type: ALERT_ERROR, payload: error.response.statusText });
      yield put({ type: TYPE + REJECTED, payload: error.response.statusText });
    }
  } else {
    yield put( { type: ALERT_ERROR, payload: 'Unknown error' });
    yield put({ type: TYPE + REJECTED, payload: 'Unknown error' });
  }
};

export const create = (TYPE, url) => function* ({ payload: data, meta: redirect }) {
  try {
    const response = yield apiRequest(`${url}`, 'POST', data);
    yield put({ type: TYPE + FULFILLED, payload: response.data });
    if (redirect) {
      history.push(redirect);
    }
  } catch (error) {
    yield handlerError(TYPE, error);
  }
};

// Whatever it was given, spelled as a query string. A key with no value is
// written out as it stands: filtering those would be this function guessing what
// a caller meant, and a caller that passes `{ x: undefined }` has a bug where the
// bug is - at the call site, in plain view.
const query = (params = {}) => {
  const pairs = Object.entries(params);
  if (pairs.length === 0) {
    return '';
  }

  return '?' + pairs.map(([k, v]) => `${k}=${v}`).join('&');
};

// A collection fetch, and the account it belongs to if it belongs to one.
//
// `scopedByAccount` comes from the duck's own declaration - see
// `baseEntitiesFactory/sagas` - so no caller has to know. The account is read
// here, out of `state.auth`, at the moment the request is made: not passed in an
// action, not held in the transport, not derived from `state.users` (which these
// very requests are what fill).
//
// Why the account is in the URL at all: the answer depends on it, so the cache
// key has to say which. A browser keeps ONE entry per URL - without this, the
// answers for two accounts overwrite each other in a single slot and every switch
// pays the whole payload again, even though the server would have said 304.
//
// A merge, and the caller wins. The scope is a DEFAULT - it is what the request
// carries when nobody said otherwise - and a default that cannot be overridden
// is not a default, it is a rule wearing the wrong name. Anything explicit at the
// call site was written by somebody who meant it.
export const fetchPaginated = (TYPE, url, scopedByAccount = false) => function* ({ payload: params }) {
  try {
    const scope = scopedByAccount
      ? { account_id: yield select(state => state.auth.account_id) }
      : {};
    const buffer = [];
    let response = yield apiRequest(url + query({ ...scope, ...params }));
    if ( Array.isArray(response.data)) {
      buffer.push(response.data);
      while (response.data.next) {
        response = yield apiRequestDirectURL(response.data.next);
        buffer.push(response.data);
      }

      // TODO to solve Nuno modification oon API to remove later when he corrects ENDPOINT


      const reducer = (acc, cv) => [...acc, ...cv.results ? cv.results : cv];
      const data = buffer.reduce(reducer, []);
      yield put({ type: TYPE + FULFILLED, payload: data });
    } else {
      yield put({ type: TYPE + FULFILLED, payload: response.data });
    }
  } catch (error) {
    yield handlerError(TYPE, error);
  }
};

export const fetch = (TYPE, url) => function* ({ payload: id }) {
  try {
    const response = yield apiRequest(id ? url + id : url);
    yield put({ type: TYPE + FULFILLED, payload: response.data, meta: { refresh: !id } });
  } catch (error) {
    yield handlerError(TYPE, error);
  }
};

export const fetchWithParams = (TYPE, url) => function* ({ payload: params, meta }) {
  try {
    let queryParams = '';
    Object.keys(params || {}).forEach(key => {
      if (key && params[key]) {
        // don't add & the first time
        if (queryParams) {
          queryParams += '&';
        }
        queryParams += `${key}=${params[key]}`;
      }
    });

    const response = yield apiRequest(queryParams ? url + '?' + queryParams : url);
    yield put({
      type: TYPE + FULFILLED,
      payload: response.data,
      meta: { refresh: (queryParams === '') || (meta && meta.forcedRefresh) }
    });
  } catch (error) {
    yield handlerError(TYPE, error);
  }
};

export const remove = (TYPE, url) => function* ({ payload: id, meta: redirect }) {
  try {
    yield apiRequest(`${url}/${id}`, 'DELETE');
    yield put({ type: TYPE + FULFILLED, payload: id });
    if (redirect) {
      history.push(redirect);
    }
  } catch (error) {
    yield handlerError(TYPE, error);
  }
};

export const update = (TYPE, url) => function* ({ payload, meta: redirect }) {
  try {
    const response = yield apiRequest(`${url}/${payload.id}`, 'PUT', payload.data);
    yield put({ type: TYPE + FULFILLED, payload: response.data });
    if (redirect) {
      history.push(redirect);
    }
  } catch (error) {
    yield handlerError(TYPE, error);
  }
};

export const searchDebounce = TYPE => function* () {
  try {
    yield delay(SEARCH_INPUT_DELAY);
    yield put({ type: TYPE });
  } catch (error) {
    console.log(error);
  }
};

export const poolApi = (TYPE, initialPath, finalPath) => function* ({ payload: id }) {
  try {
    const response = yield apiRequest(initialPath + id + finalPath);
    yield put({ type: TYPE + FULFILLED, payload: response.data });
  } catch (error) {
    yield handlerError(TYPE, error);
  }
};
